import { useMemo } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import LineArrowToRightIcon from 'LuxKit/Icons/line/LineArrowToRightIcon'
import LineLeftArrowToLeftIcon from 'LuxKit/Icons/line/LineLeftArrowToLeftIcon'
import LineDrinksIcon from 'LuxKit/Icons/line/LineDrinksIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import TextLink from 'LuxKit/TextLink'
import { getLeHotelCheckInAndOutTimes } from 'tripPlanner/utils/itemConversions/common'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  extractCancellationFromFinePrint,
  extractInclusionNotesFromFinePrint,
  extractDateChangesFromFinePrint,
} from './finePrintUtils'
import ThingsToKnowLayout from './ThingsToKnowLayout'
import type { ThingsToKnowRow } from './ThingsToKnowLayout'

import { SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import KidsFlyFreeTermsAndConditions from 'components/Common/KidsFlyFreeTermsAndConditions'
import { shouldShowKidsFlyFreeLabel } from 'lib/charterFlights/kidsFlyFreeUtils'

interface Props {
  offer: App.Offer
}

function ThingsToKnow(props: Props) {
  const { offer } = props
  const intl = useIntl()

  const {
    finePrint,
    inclusions,
    inclusionsHeading,
    property,
  } = offer

  // TEMPORARY: Extract cancellation and inclusion notes from finePrint.
  // Chain the two extractions so the remaining finePrint excludes both sections.
  const { cancellation: finePrintCancellation, remaining: finePrintAfterCancellation } = useMemo(
    () => extractCancellationFromFinePrint(finePrint),
    [finePrint],
  )
  const { inclusionNotes, remaining: finePrintAfterInclusions } = useMemo(
    () => extractInclusionNotesFromFinePrint(finePrintAfterCancellation),
    [finePrintAfterCancellation],
  )
  const { dateChanges: finePrintDateChanges, remaining: remainingFinePrint } = useMemo(
    () => extractDateChangesFromFinePrint(finePrintAfterInclusions),
    [finePrintAfterInclusions],
  )

  const inclusionsContent = inclusions ?? inclusionNotes

  const [checkInMoment, checkOutMoment] = useMemo(
    () => getLeHotelCheckInAndOutTimes(offer),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- function only reads offer.facilities
    [offer.facilities],
  )
  const checkInTime = checkInMoment?.format(SHORT_TIME_FORMAT_24_HOUR)
  const checkOutTime = checkOutMoment?.format(SHORT_TIME_FORMAT_24_HOUR)

  const checkInLabel = intl.formatMessage({
    id: 'GO5kgSlhIoJriWghX3-v',
    defaultMessage: 'Check-in from',
    description: 'Form field label for check-in the time row in things to know',
  })
  const checkOutLabel = intl.formatMessage({
    id: 'YGZtng1KK4_nCY-5nTbU',
    defaultMessage: 'Check-out by',
    description: 'Form field label for check-out time row in things to know',
  })
  const inclusionsLabel = intl.formatMessage({
    id: 'jC65d7l0oyo06NoTAVcm',
    defaultMessage: 'Inclusions',
    description: 'Fallback label for inclusions row in things to know',
  })
  const cancellationLabel = intl.formatMessage({
    id: 'EnAVJQ2V_oME-qJ91Yme',
    defaultMessage: 'Cancellation policy',
    description: 'Label for cancellation policy row in things to know',
  })
  const dateChangesLabel = intl.formatMessage({
    id: 'KXQYH9vHWqc2ztt_2Qlj',
    defaultMessage: 'Date changes',
    description: 'Form field label for date changes row in things to know',
  })
  const finePrintLabel = intl.formatMessage({
    id: 'XyfySkseacGwZyjkSAqr',
    defaultMessage: 'Fine print',
    description: 'Form field label for fine print row in things to know',
  })
  const taxesAndFeesLabel = intl.formatMessage({
    id: 'BGtki6GKP-J40skrtqA_',
    defaultMessage: 'Taxes & fees:',
    description: 'Form field label for taxes and fees section in fine print',
  })

  const cancellationContent = useMemo(() => (
    <Group direction="vertical" gap={8}>
      {offer.type === OFFER_TYPE_ALWAYS_ON && (
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="Ot0T6xeaSmIMYsZVaPIv"
            defaultMessage="Please note cancellation policies vary depending on the rate selected, please choose your package carefully. See full <link>booking terms and conditions</link>"
            description="Dynamic cancellation policy note with link to booking terms"
            values={{
              link: (chunks) => <TextLink weight="regular" to="/booking-policies#lux-premium-collection" target="_blank">{chunks}</TextLink>,
            }}
          />
        </BodyText>
      )}
      {offer.type !== OFFER_TYPE_ALWAYS_ON && finePrintCancellation && (
        <MarkdownRender type="compact" fontSize="medium" content={finePrintCancellation} />
      )}
    </Group>
  ), [offer.type, finePrintCancellation])

  const showKidsFlyFreeTC = shouldShowKidsFlyFreeLabel(offer)

  const finePrintContent = useMemo(() => (
    <Group direction="vertical" gap={12}>
      {!!property?.taxesAndFeesContent && (
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">{taxesAndFeesLabel}</BodyText>
          <MarkdownRender type="compact" fontSize="medium" content={property.taxesAndFeesContent} />
        </Group>
      )}
      <MarkdownRender type="compact" fontSize="medium" content={remainingFinePrint} />
      {showKidsFlyFreeTC && <KidsFlyFreeTermsAndConditions />}
    </Group>
  ), [property?.taxesAndFeesContent, taxesAndFeesLabel, remainingFinePrint, showKidsFlyFreeTC])

  const rows = useMemo<Array<ThingsToKnowRow>>(() => [
    ...(checkInTime ? [{
      key: 'check-in',
      icon: <LineArrowToRightIcon size="S" colour="neutral-one" />,
      label: checkInLabel,
      content: checkInTime,
      collapsable: false,
    }] : []),
    ...(checkOutTime ? [{
      key: 'check-out',
      icon: <LineLeftArrowToLeftIcon size="S" colour="neutral-one" />,
      label: checkOutLabel,
      content: checkOutTime,
      collapsable: false,
    }] : []),
    ...(inclusionsContent ? [{
      key: 'inclusions',
      icon: <LineDrinksIcon size="S" colour="neutral-one" />,
      label: inclusionsHeading ?? inclusionsLabel,
      content: <MarkdownRender type="compact" fontSize="medium" content={inclusionsContent} />,
      collapsable: true,
    }] : []),
    ...((offer.type === OFFER_TYPE_ALWAYS_ON || finePrintCancellation) ? [{
      key: 'cancellation',
      icon: <LineRefundIcon size="S" colour="neutral-one" />,
      label: cancellationLabel,
      content: cancellationContent,
      collapsable: true,
    }] : []),
    ...(finePrintDateChanges ? [{
      key: 'date-changes',
      icon: <LineCalendarIcon size="S" colour="neutral-one" />,
      label: dateChangesLabel,
      content: <MarkdownRender type="compact" fontSize="medium" content={finePrintDateChanges} />,
      collapsable: true,
    }] : []),
    ...(remainingFinePrint ? [{
      key: 'fine-print',
      icon: <LineInfoCircleIcon size="S" colour="neutral-one" />,
      label: finePrintLabel,
      content: finePrintContent,
      collapsable: true,
    }] : []),
  ], [
    checkInTime, checkInLabel, checkOutTime, checkOutLabel,
    inclusionsContent, inclusionsHeading, inclusionsLabel,
    offer.type, finePrintCancellation, cancellationLabel, cancellationContent,
    finePrintDateChanges, dateChangesLabel,
    remainingFinePrint, finePrintLabel, finePrintContent,
  ])

  return <ThingsToKnowLayout rows={rows} />
}

export default ThingsToKnow
